<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import CartSummary from './CartSummary.vue'
import CheckoutButton from '@/components/Buttons/CheckoutButton.vue'
import LoadingSpinner from '@/components/partials/Loading/LoadingSpinner.vue'
import CartItem from './CartItem.vue'
import Button from '@/components/partials/Buttons/Button/Button.vue'
import { useBasket } from '@/composables/basket'
import { useModalStore } from '@/stores/useModalStore.ts'
import { templateRef } from '@vueuse/core'
import { useCartStore } from '@/stores/useCartStore'
import type { Establishment } from "@/types/http/Establishment"
import Icon from "@/components/partials/Icon.vue"
import { storeToRefs } from "pinia"

const { t } = useI18n()
const route = useRoute()

const itemsContainer = templateRef('itemsContainer')
const hasScrollbar = ref<boolean>(false)

const emit = defineEmits([ 'confirm', 'openInstructionsModal' ])

const cartStore = useCartStore()

const { establishment, establishmentDistribution, isSyncing: cartIsSyncing, basketItems } = storeToRefs(cartStore)
const hasCartFromThisEstablishment = computed(() => cartStore.hasCart && cartStore.establishment !== null && cartStore.establishment.id === props.establishment.id)
const cartEstablishmentTitle = computed(() => establishment.value?.title ?? cartStore.basketInfo.value?.establishment?.name)

const props = withDefaults(defineProps<{
  establishment?: Establishment | null,
  noButton?: boolean,
  noStatusMessage?: boolean,
  noGradientTitle?: boolean,
}>(), {
  noButton: false,
  noStatusMessage: false,
  noGradientTitle: false,
})

const laterDate = cartStore.laterDistributionDateDescription

const {
  show,
  reset,
} = useModalStore()

const openEmptyCartConfirmationModal = () => {
  show({
    closable: true,
    dataTestId: 'empty-cart-confirmation-modal',
    title: t('confirmation-modal.title'),
    message: t('confirmation-modal.message-empty', {
      establishment: establishment.name,
    }),
    callbacks: [
      {
        label: t('confirmation-modal.cancel-btn'),
        dataTestId: 'empty-cart-confirmation-modal-cancel',
        action: () => {
          reset()
        },
        properties: {
          type: 'secondary',
        },
      },
      {
        label: t('confirmation-modal.confirm-btn'),
        dataTestId: 'empty-cart-confirmation-modal-confirm',
        action: async () => {
          await emptyBasket()
          reset()
        },
      },
    ],
  })
}

const allHaveImages = computed<boolean>(() => basketItems.value.every((item) => !!item.product.small_image_url))
const withCartOnCheckoutPage = computed<boolean>(() => cartStore.hasCart && route.name.includes('checkout'))

const basket = useBasket()

async function emptyBasket() {
  await basket.emptyBasket()
}

const { changeProductAmountInBasket } = useBasket()

async function changeAmount({
                              hash,
                              amount,
                            }) {
  const items = cartStore.basketItems
  const willEmptyBasket = items?.length === 1 && amount === 0

  // do not show modal if basket will not be emptied or on establishment page
  if (!willEmptyBasket) {
    await changeProductAmountInBasket(hash, amount)
    return
  }

  // On establishment pages, do not show an empty basket confirm modal
  if (route.name.includes('establishment')) {
    await emptyBasket()

    return
  }

  // Wait for confirmation
  // This will modal also empty the basket
  openEmptyCartConfirmationModal('empty')
}

const checkScrollbar = () => {
  const outer = document.createElement('div')
  outer.style.visibility = 'hidden'
  outer.style.overflow = 'scroll' // forcing scrollbar to appear
  outer.style.msOverflowStyle = 'scrollbar' // needed for WinJS apps
  document.body.appendChild(outer)

  // Creating inner element and placing it in the container
  const inner = document.createElement('div')
  outer.appendChild(inner)

  // Calculating difference between container's full width and the child width
  const scrollbarWidth = (outer.offsetWidth - inner.offsetWidth)

  // Removing temporary elements from the DOM
  outer.parentNode.removeChild(outer)

  hasScrollbar.value = itemsContainer.value?.scrollHeight > itemsContainer.value?.clientHeight && scrollbarWidth > 0
}

let resizeObserver = null

onMounted(() => {
  if (itemsContainer.value !== null) {
    resizeObserver = new ResizeObserver(checkScrollbar)
    resizeObserver.observe(itemsContainer.value)
  }
})

onBeforeUnmount(() => {
  if (resizeObserver !== null) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
})

function openInstructionsModal(product) {
  emit('openInstructionsModal', product)
}
</script>

<template>
  <div ref="cartElement"
       :class="[ 'cart', { 'cart--no-gradient-title': props.noGradientTitle, 'cart--has-scrollbar': hasScrollbar } ]"
       data-test-id="cart-desktop">
    <template v-if="(cartStore.hasCart && hasCartFromThisEstablishment) || withCartOnCheckoutPage">
      <div class="cart__content">
        <div class="status-message"
             v-if="establishmentDistribution !== null && establishmentDistribution.overlay.overlay !== null && !laterDate">
          <Icon name="red-circle-exclamationmark"/>
          {{ establishmentDistribution?.overlay.overlay }}
        </div>

        <div class="cart__title">
          {{ t('cart.shopping_cart') }}
          <LoadingSpinner v-if="cartIsSyncing"
                          class="cart__spinner"
                          :size="16"
          />
        </div>

        <div ref="itemsContainer"
             class="cart__items"
        >
          <CartItem v-for="item in basketItems"
                    :amount="item.amount"
                    :product="item.product"
                    class="cart__item"
                    :with-image="allHaveImages"
                    @change-amount="changeAmount"
                    @add-instructions="openInstructionsModal(item.product)"
                    :allows-instructions="establishment?.instructions_mode === 'once_and_per_item'"
          />
        </div>

        <CartSummary class="cart__summary"/>
      </div>

      <div v-if="!noButton"
           class="cart__chin"
      >
        <CheckoutButton v-taptic="'medium'"
                        :can-order="establishmentDistribution?.overlay.can_order ?? false"
                        data-test-id="sidebar-checkout-button"
                        @click="emit('confirm')"
        />
      </div>
    </template>
    <template v-else-if="cartStore.hasCart && !hasCartFromThisEstablishment">
      <div class="cart__empty-basket-message">
        <div>
          <div v-if="establishment" class="cart__empty-basket-message__title">
            {{ t('cart.swapping_restaurants') }}
          </div>
          <div class="cart__empty-basket-message__subtitle">
            {{ t('cart.cart_will_be_emptied', { establishment: cartEstablishmentTitle }) }}
          </div>
          <div class="cart__empty-basket-message__button">
            <Button @click="emptyBasket">
              {{ t('cart.empty_cart') }}
            </Button>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="cart__content">
        <div class="cart__title">
          {{ t('cart.shopping_cart') }}
          <LoadingSpinner v-if="cartIsSyncing"
                          class="cart__spinner"
                          :size="16"
          />
        </div>
        {{ t('cart.your_shopping_cart_is_empty') }}
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/css/mixins/styling.scss";
@import "@/assets/css/mixins/breakpoints-up.scss";

.cart {
  $self:      &;
  @include shadow-md;
  border:     1px solid #dee0e4;
  border-radius: 1rem;
  align-self: flex-start;
  position:   relative;

  &__content {
    padding: 1.5rem;
  }

  &__empty-basket-message {
    width:       100%;
    height:      100%;
    display:     flex;
    align-items: center;
    justify-content: center;
    padding:     2rem;

    &__title {
      font-weight: 700;
      font-size: 1.25rem;
      width:     max-content;
    }

    &__subtitle {
      margin-top: 0.5rem;
    }

    &__button {
      margin-top: 1.5rem;
    }
  }

  &__title {
    @include gradient-text;
    font-weight: 700;
    font-size:   1.5rem;
    line-height: 150%;
    width:       max-content;
    user-select: none;
    display:     flex;
    align-items: center;
    justify-content: space-between;
    width:       100%;
  }

  &__items {
    display:      flex;
    flex-direction: column;
    gap:          2rem;
    margin-right: -1.5rem;
    padding:      1.5rem 1.5rem 2rem 0;
    align-items:  stretch;
    max-height:   calc(100vh - 34rem);
    overflow-y:   auto;
    overflow-x:   hidden;

    @include lg-up {
      min-height: 12rem;
    }
  }

  &__chin {
    background-color:          #f8f8fa;
    padding:                   1.5rem;
    border-bottom-left-radius: 1rem;
    border-bottom-right-radius: 1rem;

    &:deep(.checkout-btn) {
      width: max-content;
      margin: 0 auto;
    }
  }

  &--no-gradient-title {
    #{$self}__title {
      color:           var(--color-neutral-primary);
      -webkit-text-fill-color: var(--color-neutral-primary);
      background:      transparent;
      background-clip: content-box;
    }
  }

  &--has-scrollbar {
    #{$self}__items {
      padding: 1.5rem 0.5rem 2rem 0;
      position: relative;
    }

    #{$self}__summary {
      padding-top: 1rem;
    }
  }
}

.status-message {
  display:          flex;
  justify-content:  center;
  align-items:      center;
  gap:              0.5rem;
  background-color: var(--color-brand-primary-10);
  padding:          0.5rem 0.5rem;
  border-radius:    0.5rem;
  font-size:        0.875rem;
  margin-bottom:    1rem;

  &__bold {
    font-weight: 700;
  }
}
</style>
